import { ANNUAL_DISCOUNT_PERCENT, BILLING_TRIAL_DAYS, type BillingInterval } from 'definitions/billing-plans';
import { useAgencyFee } from '@/state/billing/useAgencyFee';
import { formatEuro } from '@/state/billing/billing-format';
import { usePlanStep } from '@/state/onboarding/usePlanStep';
import { trackUmami } from '@/lib/umami/umami';
import { PillTabs } from '../Dashboard/ConsolePanel';
import { AgencyFeePicker } from '../Dashboard/Settings/Billing/AgencyFeePicker';
import { OnboardingFrame } from './plan/OnboardingFrame';
import { PlanCards } from '../Dashboard/Settings/Billing/PlanCards';
import { PlanFaq } from './plan/PlanFaq';
import { StepHeading } from './plan/StepHeading';
import { TrafficSlider } from './plan/TrafficSlider';
import { PlanSummary, type TimelineRow } from '../Dashboard/Settings/Billing/PlanSummary';
import {
  agencyFeeMonthlyEquivalent,
  agencyFeeYearlySaving,
  firstChargeDate,
  formatShortDate,
} from '@/state/onboarding/plan-model';

type PlanStepProps = { isAgency: boolean; stepCount: number };

const INTERVAL_OPTIONS = [
  { label: 'Monthly', value: 'month' },
  { label: `Yearly · save ${ANNUAL_DISCOUNT_PERCENT}%`, value: 'year' },
] as const;

const TRIAL_NOTE =
  "You'll add a card on the next step. Nothing is charged during the trial, and you can cancel anytime before it ends.";
const NO_TRIAL_NOTE = "You'll add a card on the next step. You can cancel anytime from Settings.";
const UNAVAILABLE_NOTE = 'Card payments are not available yet. Please check back soon.';

/**
 * Step 1 of a new account's setup: choosing the plan the free trial starts on, or, for an
 * agency, the agency fee. The button sends the Owner or Admin to Polar's checkout and
 * back; when the subscription is confirmed the flow moves on (useCheckoutReturn, mounted
 * by OnboardingScreen).
 */
export const PlanStep = ({ isAgency, stepCount }: PlanStepProps) =>
  isAgency ? <AgencyFeeStep stepCount={stepCount} /> : <AccountPlanStep stepCount={stepCount} />;

type StepProps = { stepCount: number };

const AccountPlanStep = ({ stepCount }: StepProps) => {
  const plan = usePlanStep();
  const yearly = plan.interval === 'year';

  const rows: TimelineRow[] = plan.trialAvailable
    ? [
        { when: 'Today', what: 'Full access, install the 0.9 KB tracker', amount: '€0', emphasis: true },
        { when: plan.chargeDate, what: 'First charge, unless you cancel', amount: formatEuro(plan.firstCharge) },
      ]
    : [
        {
          when: 'Today',
          what: 'First charge, then every ' + (yearly ? 'year' : 'month'),
          amount: formatEuro(plan.firstCharge),
          emphasis: true,
        },
      ];

  return (
    <OnboardingFrame>
      <StepHeading
        step={1}
        of={stepCount}
        stepName="Choose a plan"
        title="Choose your plan"
        intro="Pick the plan that fits your traffic and start your 12-day free trial. Every plan includes unlimited websites, users and experiments, and you can change plan at any time."
      />

      <div className="flex flex-wrap items-end gap-x-8 gap-y-[18px]">
        <TrafficSlider value={plan.trafficIndex} onChange={plan.setTrafficIndex} />
        <div className="flex flex-col gap-[7px] pb-[22px]">
          <span className="micro-label">Billing</span>
          <div className="whitespace-nowrap">
            <PillTabs
              options={INTERVAL_OPTIONS}
              value={plan.interval}
              onChange={(value) => plan.setInterval(value === 'month' ? 'month' : 'year')}
            />
          </div>
        </div>
      </div>

      <PlanCards
        selected={plan.selected}
        recommended={plan.recommended}
        interval={plan.interval}
        onSelect={plan.selectPlan}
      />

      <PlanSummary
        label={plan.trialAvailable ? 'Your trial' : 'Your plan'}
        title={
          plan.trialAvailable ? `${plan.selectedName}, free for ${BILLING_TRIAL_DAYS} days.` : `${plan.selectedName}.`
        }
        line={
          yearly
            ? `${plan.trialAvailable ? 'Then ' : ''}${formatEuro(plan.perMonth)} a month, billed yearly, excl. VAT. That is ${formatEuro(plan.saving)} less than paying monthly.`
            : `${plan.trialAvailable ? 'Then ' : ''}${formatEuro(plan.perMonth)} a month, excl. VAT. Pay yearly and save ${formatEuro(plan.saving)}.`
        }
        rows={rows}
        ctaLabel={plan.trialAvailable ? `Start ${BILLING_TRIAL_DAYS}-day free trial` : 'Continue to checkout'}
        note={!plan.checkoutAvailable ? UNAVAILABLE_NOTE : plan.trialAvailable ? TRIAL_NOTE : NO_TRIAL_NOTE}
        showPaymentMethods={plan.checkoutAvailable}
        disabled={!plan.checkoutAvailable}
        isPending={plan.isStarting}
        onContinue={() => {
          // The button stays clickable while the checkout is created (it only shows a spinner);
          // a second click would count this step twice. startCheckout ignores it anyway.
          if (plan.isStarting) return;
          // The plan and interval chosen are what this step exists to optimise.
          trackUmami('checkout-started', { type: 'client', plan: plan.selected, interval: plan.interval });
          plan.start();
        }}
      />

      <PlanFaq />
    </OnboardingFrame>
  );
};

const AgencyFeeStep = ({ stepCount }: StepProps) => {
  const fee = useAgencyFee();
  const yearly = fee.interval === 'year';
  const chargeDate = formatShortDate(firstChargeDate(new Date()));
  const saving = agencyFeeYearlySaving();

  const rows: TimelineRow[] = fee.trialAvailable
    ? [
        { when: 'Today', what: 'Full access, your own website included', amount: '€0', emphasis: true },
        { when: chargeDate, what: 'First charge, unless you cancel', amount: formatEuro(fee.periodPrice) },
      ]
    : [{ when: 'Today', what: 'First charge', amount: formatEuro(fee.periodPrice), emphasis: true }];

  return (
    <OnboardingFrame>
      <StepHeading
        step={1}
        of={stepCount}
        stepName="Choose the agency fee"
        title="Choose the agency fee"
        intro="The agency features, with a 12-day free trial. We set up an account for your own business, included in the fee, with its own website, 100,000 sessions and 2,000 AI credits a month. Each client you add has its own plan, billed to you as usual, and you choose it when you add the client."
      />

      <div className="flex flex-col gap-[7px]">
        <span className="micro-label">Billing</span>
        <div className="whitespace-nowrap">
          <PillTabs
            options={INTERVAL_OPTIONS}
            value={fee.interval}
            onChange={(value) => fee.setInterval((value === 'month' ? 'month' : 'year') satisfies BillingInterval)}
          />
        </div>
      </div>

      <PlanSummary
        label={fee.trialAvailable ? 'Your trial' : 'Your agency fee'}
        title={fee.trialAvailable ? `Agency, free for ${BILLING_TRIAL_DAYS} days.` : 'Agency.'}
        line={
          yearly
            ? `${fee.trialAvailable ? 'Then ' : ''}${formatEuro(agencyFeeMonthlyEquivalent(fee.interval))} a month, billed yearly, excl. VAT. That is ${formatEuro(saving)} less than paying monthly.`
            : `${fee.trialAvailable ? 'Then ' : ''}${formatEuro(fee.periodPrice)} a month, excl. VAT. Pay yearly and save ${formatEuro(saving)}.`
        }
        rows={rows}
        ctaLabel={fee.trialAvailable ? `Start ${BILLING_TRIAL_DAYS}-day free trial` : 'Continue to checkout'}
        note={!fee.checkoutAvailable ? UNAVAILABLE_NOTE : fee.trialAvailable ? TRIAL_NOTE : NO_TRIAL_NOTE}
        showPaymentMethods={fee.checkoutAvailable}
        disabled={!fee.checkoutAvailable}
        isPending={fee.isStarting}
        onContinue={() => {
          if (fee.isStarting) return;
          trackUmami('checkout-started', { type: 'agency', plan: 'agency-fee', interval: fee.interval });
          fee.start();
        }}
      />

      <PlanFaq />
    </OnboardingFrame>
  );
};
